import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Select, MenuItem, FormControl } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { AppTheme } from '../../../../theme/App.theme';
import './HorizontalCalendar.css';

const HorizontalCalendar = ({ 
  selectedDate, 
  onDateSelect,
  eventDates = [], // Array of date strings (e.g., ["Oct 21 2024", "Oct 24 2024"])
  className = "",
  style = {}
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [showValidationMessage, setShowValidationMessage] = useState(false);

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Helper function to check if a date has an event
  const hasEvent = (date) => {
    if (!eventDates || eventDates.length === 0) return false;
    
    // Format the date to match the format in eventDates array
    const dateStr = date.toDateString(); // e.g., "Thu Oct 21 2024"
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formattedDate = `${monthNames[date.getMonth()]} ${String(date.getDate()).padStart(2, '0')} ${date.getFullYear()}`; // e.g., "Oct 21 2024"
    
    return eventDates.some(eventDate => {
      // Try different date formats for comparison
      return eventDate === dateStr || 
             eventDate === formattedDate ||
             eventDate.includes(`${monthNames[date.getMonth()]} ${String(date.getDate()).padStart(2, '0')} ${date.getFullYear()}`) ||
             eventDate.includes(`${monthNames[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}`);
    });
  };

  // Generate dates for the current month only
  const generateDates = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    
    const dates = [];
    const today = new Date();
    const selectedDateObj = selectedDate ? new Date(selectedDate) : null;
    
    // Only generate dates for current month
    for (let i = 1; i <= lastDay; i++) {
      const date = new Date(year, month, i);
      
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = selectedDateObj && date.toDateString() === selectedDateObj.toDateString();
      const hasEventIndicator = hasEvent(date);
      
      dates.push({
        date: i,
        fullDate: new Date(date),
        isCurrentMonth: true,
        isToday,
        isSelected,
        hasEvent: hasEventIndicator,
        dayName: days[date.getDay()]
      });
    }
    
    return dates;
  };

  // Generate years array (current year ± 50 years)
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 50; i <= currentYear + 50; i++) {
      years.push(i);
    }
    return years;
  };

  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setCurrentMonth(new Date(currentMonth.getFullYear(), newMonth, 1));
    setShowValidationMessage(true);
  };

  const handleYearChange = (event) => {
    const newYear = event.target.value;
    setCurrentMonth(new Date(newYear, currentMonth.getMonth(), 1));
    setShowValidationMessage(true);
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const handleDateClick = (dateObj) => {
    if (onDateSelect) {
      onDateSelect(dateObj.fullDate);
      setShowValidationMessage(false); // Clear validation message when date is selected
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Add scroll event listener
      container.addEventListener('scroll', handleScroll);
      
      // Initial scroll check
      handleScroll();

      // Check scroll on window resize for responsive behavior
      const checkResponsiveScroll = () => {
        const viewportWidth = window.innerWidth;
        
        // Adjust scroll behavior based on device width
        if (viewportWidth <= 600) {
          // Mobile
          container.style.scrollBehavior = 'smooth';
          container.style.scrollSnapType = 'x mandatory';
        } else if (viewportWidth <= 960) {
          // Tablet
          container.style.scrollBehavior = 'smooth';
          container.style.scrollSnapType = 'x proximity';
        } else {
          // Desktop
          container.style.scrollBehavior = 'smooth';
          container.style.scrollSnapType = 'none';
        }
        
        handleScroll();
      };

      window.addEventListener('resize', checkResponsiveScroll);
      checkResponsiveScroll(); // Initial check

      // Cleanup
      return () => {
        container.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', checkResponsiveScroll);
      };
    }
  }, []);

  // Clear validation message when selected date is in the current month/year
  useEffect(() => {
    if (selectedDate) {
      const selectedDateObj = new Date(selectedDate);
      const currentMonthYear = currentMonth.getMonth() === selectedDateObj.getMonth() && 
                               currentMonth.getFullYear() === selectedDateObj.getFullYear();
      
      if (currentMonthYear) {
        setShowValidationMessage(false);
      }
    }
  }, [selectedDate, currentMonth]);

  // Scroll to current date on initial load and when month changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && selectedDate) {
      const selectedDateObj = new Date(selectedDate);
      const currentMonthYear = currentMonth.getMonth() === selectedDateObj.getMonth() && 
                               currentMonth.getFullYear() === selectedDateObj.getFullYear();
      
      if (currentMonthYear) {
        const scrollToDate = () => {
          // Get viewport width to determine device type
          const viewportWidth = window.innerWidth;
          
          // Calculate date dimensions based on device size
          let dateWidth, gap;
          if (viewportWidth <= 600) {
            // Mobile
            dateWidth = 60;
            gap = 8;
          } else if (viewportWidth <= 960) {
            // Tablet
            dateWidth = 70;
            gap = 12;
          } else {
            // Desktop
            dateWidth = 80;
            gap = 12;
          }
          
          // Calculate the position of the selected date
          const dateIndex = selectedDateObj.getDate() - 1;
          const itemWidth = dateWidth + gap;
          const containerWidth = container.clientWidth;
          
          // Calculate scroll position to center the date
          const scrollPosition = (itemWidth * dateIndex) - (containerWidth / 2) + (dateWidth / 2);
          
          // Scroll to position
          container.scrollTo({
            left: Math.max(0, scrollPosition),
            behavior: 'smooth'
          });
        };
        
        // Use requestAnimationFrame for better performance and reliability
        setTimeout(() => {
          requestAnimationFrame(scrollToDate);
        }, 150);
      }
    }
  }, [currentMonth, selectedDate]);

  const dates = generateDates();

  return (
    <Box className={`horizontal-calendar-container ${className}`} style={style}>
      {/* Month and Year Selector */}
      <Box 
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: { xs: '12px', sm: '16px' },
          marginBottom: '10px',
          padding: { xs: '0 16px', sm: '0 20px' },
          flexWrap: 'wrap'
        }}
      >
        {/* Month Selector */}
        <FormControl 
          sx={{ 
            minWidth: { xs: '140px', sm: '160px' }
          }}
        >
          <Select
            value={currentMonth.getMonth()}
            onChange={handleMonthChange}
            size='small'
            sx={{
              fontWeight: 600,
              fontSize: { xs: '14px', sm: '16px' },
              color: AppTheme.PRIMARY_BLACK,
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: AppTheme.PRIMARY_COLOR,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: AppTheme.PRIMARY_COLOR,
              }
            }}
          >
            {months.map((month, index) => (
              <MenuItem key={index} value={index}>
                {month}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Year Selector */}
        <FormControl 
          sx={{ 
            minWidth: { xs: '100px', sm: '120px' }
          }}
        >
          <Select
            value={currentMonth.getFullYear()}
            onChange={handleYearChange}
            size='small'
            sx={{
              fontWeight: 600,
              fontSize: { xs: '14px', sm: '16px' },
              color: AppTheme.PRIMARY_BLACK,
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: AppTheme.PRIMARY_COLOR,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: AppTheme.PRIMARY_COLOR,
              }
            }}
          >
            {generateYears().map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Validation Message */}
      {showValidationMessage && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: { xs: '0 16px', sm: '0 20px' },
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              color: AppTheme.PRIMARY_COLOR,
              textAlign: 'left',
              backgroundColor: AppTheme.PRIMARY_LIGHT,
              padding: "3px 5px",
              borderRadius: '4px',
              border: `1px solid ${AppTheme.PRIMARY_COLOR}`,
            }}
          >
            Please select a date to view Panchang details.
          </Typography>
        </Box>
      )}

      {/* Calendar Container */}
      <Box 
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          padding: { xs: '0 16px', sm: '0 20px' }
        }}
      >
        {/* Left Arrow */}
        {showLeftArrow && (
          <IconButton
            onClick={scrollLeft}
            sx={{
              position: 'absolute',
              left: { xs: '4px', sm: '8px' },
              zIndex: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              }
            }}
          >
            <ArrowBackIosIcon />
          </IconButton>
        )}

        {/* Calendar Scroll Container */}
        <Box
          ref={scrollContainerRef}
          className="horizontal-calendar-scroll"
          sx={{
            display: 'flex',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            gap: { xs: '8px', sm: '12px' },
            padding: { xs: '8px 0', sm: '12px 0' },
            width: '100%'
          }}
        >
          {dates.map((dateObj, index) => (
            <Box
              key={index}
              onClick={() => handleDateClick(dateObj)}
              className={`calendar-date-item ${dateObj.isSelected ? 'selected' : ''}`}
              sx={{
                minWidth: { xs: '60px', sm: '70px', md: '80px' },
                height: { xs: '70px', sm: '80px', md: '90px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: { xs: '12px', sm: '14px', md: '16px' },
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                backgroundColor: dateObj.isSelected 
                  ? '#FDB022' 
                  : dateObj.isToday 
                    ? 'transparent' 
                    : 'transparent',
                border: dateObj.isSelected 
                  ? '2px solid #FDB022' 
                  : dateObj.isToday 
                    ? '2px solid #FDB022' 
                    : '2px solid transparent',
                '&:hover': {
                  backgroundColor: dateObj.isSelected 
                    ? '#FDB022' 
                    : '#f8f9fa',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '10px', sm: '11px', md: '12px' },
                  fontWeight: 500,
                  color: dateObj.isSelected 
                    ? '#FFFFFF' 
                    : dateObj.isToday 
                      ? '#000000' 
                      : '#6B7280',
                }}
              >
                {dateObj.dayName}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '16px', sm: '18px', md: '20px' },
                  fontWeight: 700,
                  color: dateObj.isSelected 
                    ? '#FFFFFF' 
                    : dateObj.isToday 
                      ? '#000000' 
                      : '#000000'
                }}
              >
                {dateObj.date}
              </Typography>
              
              {/* Event Indicator Dot */}
              {dateObj.hasEvent && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '6px',
                    padding: "2px",
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#D90000',
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        {/* Right Arrow */}
        {showRightArrow && (
          <IconButton
            onClick={scrollRight}
            sx={{
              position: 'absolute',
              right: { xs: '4px', sm: '8px' },
              zIndex: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              }
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default HorizontalCalendar;
